import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Send, CheckCircle2, Instagram, Facebook } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INSTAGRAM_URL = "https://www.instagram.com/takomstmenaam/";
const FACEBOOK_URL = "https://www.facebook.com/profile.php?id=61591858686559";

export default function Contact() {
  const [form, setForm] = useState({ naam: "", email: "", bericht: "" });
  const [nieuwsbrief, setNieuwsbrief] = useState(false);
  const [status, setStatus] = useState(null); // null | "sending" | "ok"

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.naam || !form.email || !form.bericht) {
      toast.error("Vul alle velden in voordat je verstuurt.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, nieuwsbrief });
      setStatus("ok");
      toast.success("Bericht verstuurd! We nemen zo snel mogelijk contact op.");
      setForm({ naam: "", email: "", bericht: "" });
      setNieuwsbrief(false);
    } catch (err) {
      setStatus(null);
      toast.error("Er ging iets mis bij het versturen. Probeer het opnieuw.");
    }
  }

  const inputClass =
    "w-full px-4 py-3 rounded-lg bg-white/5 border border-white/15 text-[#FAF7F2] placeholder:text-[#FAF7F2]/35 text-sm outline-none focus:border-[#74C69D] transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-[#16271d]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Intro */}
          <Reveal>
            <Eyebrow dark testid="contact-eyebrow">Contact</Eyebrow>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#FAF7F2] mb-5">
              Idee of vraag?
            </h2>
            <p className="text-[#FAF7F2]/65 leading-relaxed mb-7 max-w-md">
              Neem contact op met de werkgroep Takomst fan Menaam. We lezen alles en
              reageren zo snel mogelijk.
            </p>
            <div className="border-l-2 border-[#74C69D] pl-5">
              <p className="text-sm text-[#FAF7F2]/60 leading-relaxed m-0">
                Je kunt ook langskomen bij een dorpsvergadering of je aanmelden voor de
                nieuwsbrief via het formulier.
              </p>
            </div>

            <div className="mt-8 flex flex-col gap-3">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 hover:bg-white/10 hover:border-[#74C69D]/40 transition-colors"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#74C69D]/15 text-[#74C69D] group-hover:bg-[#74C69D]/25 transition-colors">
                  <Instagram size={18} />
                </span>
                <span>
                  <span className="block text-sm font-bold text-[#FAF7F2]">Volg @takomstmenaam</span>
                  <span className="block text-xs text-[#FAF7F2]/55">Foto's, updates en achter de schermen</span>
                </span>
              </a>

              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook"
                className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 hover:bg-white/10 hover:border-[#74C69D]/40 transition-colors"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#74C69D]/15 text-[#74C69D] group-hover:bg-[#74C69D]/25 transition-colors">
                  <Facebook size={18} />
                </span>
                <span>
                  <span className="block text-sm font-bold text-[#FAF7F2]">Vind ons op Facebook</span>
                  <span className="block text-xs text-[#FAF7F2]/55">Volg onze updates en berichten</span>
                </span>
              </a>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1}>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#FAF7F2]/85 mb-1.5">Je naam</label>
                <input
                  name="naam"
                  value={form.naam}
                  onChange={handleChange}
                  placeholder="Voornaam Achternaam"
                  data-testid="contact-input-naam"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#FAF7F2]/85 mb-1.5">E-mailadres</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="jij@voorbeeld.nl"
                  data-testid="contact-input-email"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#FAF7F2]/85 mb-1.5">Bericht of idee</label>
                <textarea
                  name="bericht"
                  value={form.bericht}
                  onChange={handleChange}
                  placeholder="Schrijf hier je vraag, idee of opmerking..."
                  rows={5}
                  data-testid="contact-input-bericht"
                  className={`${inputClass} resize-y`}
                />
              </div>
              <label className="flex items-center gap-2.5 text-sm text-[#FAF7F2]/70 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={nieuwsbrief}
                  onChange={(e) => setNieuwsbrief(e.target.checked)}
                  data-testid="contact-newsletter-checkbox"
                  className="w-4 h-4 accent-[#74C69D]"
                />
                Schrijf me in voor de nieuwsbrief
              </label>

              {status === "ok" && (
                <div
                  data-testid="contact-success"
                  className="flex items-center gap-2 rounded-lg bg-[#74C69D]/10 border border-[#74C69D]/30 px-4 py-3"
                >
                  <CheckCircle2 size={18} className="text-[#74C69D]" />
                  <p className="text-sm font-semibold text-[#74C69D] m-0">
                    Bericht verstuurd! We nemen zo snel mogelijk contact op.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="contact-submit-button"
                className="mt-1 inline-flex items-center justify-center gap-2 w-full font-bold text-sm px-7 py-3.5 rounded-full bg-[#74C69D] text-[#1A2E22] hover:bg-[#8fd4b0] transition-colors disabled:opacity-70 disabled:cursor-wait"
              >
                {status === "sending" ? "Bezig met versturen…" : "Verstuur bericht"}
                {status !== "sending" && <Send size={16} />}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
